import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import AdvanceFileInput from 'components/field/AdvanceFileInput';
import Button from 'components/field/Button';
import TextArea from 'components/field/TextArea';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Alert from 'components/global/Alert';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import useAccessControl from 'hooks/useAccessControl';
import useAlert from 'hooks/useAlert';
import useAppTracking from 'hooks/useAppTracking';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import { importActions } from 'pages/Settings/ImportExport/ImportLog/ImportLogList';

import { WAREHOUSES } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import PATHS from 'utils/paths';

/**
 * @muhammedburakak
 * @description File import modal component.
 * @param {Object} props - props of the component.
 * @param {string} [props.title] - title of the modal
 * @param {Object} [props.icon] - Icon for the modal.
 * @param {Object} [props.downloadLink] - Download template link.
 */

const ImportDataModal = NiceModal.create(
	({ title, downloadUrl, instruction, uploadUrl, acceptedFileTypes, alertMsg = null, setTableData }) => {
		// State & Hooks
		const modal = useModal();
		const { track } = useAppTracking();

		const { showError, showSuccess, showWarning } = useAlert();
		const [isLoading, setIsLoading] = useState(false);
		const [isDownloading, setIsDownloading] = useState(false);
		const axiosPrivate = useAxiosPrivate();
		const navigate = useNavigate();
		const isAuthorized = useAccessControl();

		const ImportSchema = useMemo(
			() =>
				yup.object().shape({
					description: yup.string().nullable(),
					importFile: yup.object().shape({
						name: yup.string().nullable().required(),
						path: yup.string().nullable().required()
					})
				}),
			[]
		);

		// Form methods
		const formMethods = useForm({
			resolver: yupResolver(ImportSchema),
			defaultValues: {
				importFile: {
					name: null,
					path: null
				},
				description: null
			}
		});

		const {
			handleSubmit,
			setValue,
			watch,
			formState: { isDirty }
		} = formMethods;

		// Helpers
		const checkPickupLocation = useCallback(async () => {
			setIsLoading(true);
			await axiosPrivate
				.post(WAREHOUSES.GET_PICKUP_LOCATION)
				.then((response) => {
					if (response.status === 200) {
						// do nothing
					} else if (response.status === 204) {
						// send a warning to user
						showWarning({
							message: 'orderImport.pickupLocation',
							link: 'selectPickupLocation',
							linkAction: () => {
								modal.remove();
								isAuthorized(PATHS.PICKUP_LOCATION)
									? navigate(PATHS.PICKUP_LOCATION)
									: navigate(PATHS.WAREHOUSES);
							}
						});
					} else {
						AppLogger.error(
							`ImportDataModal : Error while getting pickup location: ${JSON.stringify(response)}`
						);
					}
				})
				.catch((error) => {
					AppLogger.error(`ImportDataModal : Error while getting pickup location: ${JSON.stringify(error)}`);
				})
				.finally(() => setIsLoading(false));
		}, []);

		const handleDownloadTemplate = useCallback(
			async (actionLink) => {
				if (!actionLink) {
					showError({
						message: 'errorMsg.fileNotFound'
					});
					AppLogger.error(`ImportDataModal : Missing file url for ${title} download.`);
					return;
				}
				setIsDownloading(true);
				try {
					const response = await axiosPrivate.post(actionLink);

					if (response.status === 200) {
						if (!response.data?.file || !response.data?.path || !response.data?.language) {
							showError({
								message: 'errorMsg.missingFileInfo'
							});
							AppLogger.error(
								`ImportDataModal : Missing file info came from ${actionLink} Error log : ${JSON.stringify(
									response
								)}`
							);
						} else {
							const url = `${response.data.path}${response.data.language}/${response.data.file}`;
							const link = document.createElement('a');
							link.href = url;
							document.body.appendChild(link);

							link.click();
							link.parentNode.removeChild(link);
						}
					} else {
						showError({
							message: 'errorMsg.fileNotFound'
						});
						AppLogger.error(
							`ImportDataModal : Error while getting the file info from ${actionLink} Error log : ${JSON.stringify(
								response
							)}`
						);
					}
				} catch (error) {
					showError();
					AppLogger.error(
						`ImportDataModal : Error while getting the file info from ${actionLink} Error log : ${JSON.stringify(
							error
						)}`
					);
				} finally {
					setIsDownloading(false);
				}
			},
			[title]
		);

		const onSubmit = ({ description, importFile }) => {
			setIsLoading(true);
			axiosPrivate
				.post(uploadUrl, { description, importFile })
				.then((response) => {
					const { status, data } = response;
					track('importAdded', { importType: title, status }, ['mixpanel']);
					if (status === 200) {
						showSuccess();
						setTableData && setTableData(data);
						modal.remove();
					} else {
						showError();
						AppLogger.error(
							`ImportDataModal : Error while importing the file ${JSON.stringify(
								importFile
							)} to : ${uploadUrl} Error log : ${JSON.stringify(response)}`
						);
					}
				})
				.catch((error) => {
					showError();
					AppLogger.error(
						`ImportDataModal : Error while importing the file ${JSON.stringify(
							importFile
						)} to : ${uploadUrl} Error log : ${JSON.stringify(error)}`
					);
				})
				.finally(() => setIsLoading(false));
		};

		useEffect(() => {
			if (title === importActions.orderImport.title) {
				checkPickupLocation();
			}
		}, [title]);

		// Render
		return (
			<Modal fullwidth maxWidth="medium" className="modal-importDataModal">
				<ModalTitle>
					<Typography variant="p1">{title}</Typography>
				</ModalTitle>

				{downloadUrl && (
					<Alert
						content="importDataDescription"
						className="mx-3 mb-5"
						ctaButtonLabel="downloadTemplate"
						ctaButtonLoading={isDownloading}
						onCtaButtonClick={() => handleDownloadTemplate(downloadUrl)}
						showIcon
					/>
				)}

				{instruction && <Alert showIcon content={instruction} className="mx-3 mb-5" />}

				<ModalContent>
					<Form formMethods={formMethods} borderless style={{ justifyContent: 'center' }}>
						<FormItem label={'uploadFile'} isLoading={isLoading} required>
							<AdvanceFileInput
								name="importFile"
								acceptedFileType={acceptedFileTypes?.join(',')}
								setValue={(value) => {
									setValue('importFile', value, { shouldValidate: true, shouldDirty: true });
								}}
								value={watch('importFile')}
								maxFileSize={1}
								fileLimit={1}
							/>
						</FormItem>

						{alertMsg && <Alert content={alertMsg} className="m-3" showIcon />}

						<FormItem label={'description'} isLoading={isLoading}>
							<TextArea name="description" label="description" placeholder="description" />
						</FormItem>
					</Form>
				</ModalContent>

				<ModalActions>
					<Button label="cancel" variant="borderless" onClick={() => modal.remove()} />

					<Button
						variant="blue"
						label="importFile"
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
						disabled={!isDirty || isLoading}
					/>
				</ModalActions>
			</Modal>
		);
	}
);

export default ImportDataModal;
